// ─────────────────────────────────────────────────────────────────────────────
// SettingsDrawer, the gear-icon Settings drawer, ported 1:1 from app.html's
// #settingsPanel markup + site/js/settings-wiring.js + site/js/connectors-wiring.js.
// Four tabs: Artist Profile, Connections, Account, Features. CSS is the verbatim
// legacy css/settings.css (co-located as ./settings.css). Role-based tab hiding
// mirrors the data-hide-for-* attributes in the legacy markup.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { User } from '../../lib';
import {
  type BandProfile,
  type ComparableArtist,
  type ConnBlob,
  PROFILE_TEXT_FIELDS,
  getProfileLocal,
  saveProfile,
  fetchCloudProfile,
  profileHasContent,
  fmtListeners,
  validateArtist,
  changePassword,
  saveOpenToSupport,
  planLabel,
  memberSince,
  fetchConnBlob,
  saveConnBlob,
  connView,
  parseSpotifyId,
  parseYouTube,
  parseBandsintown,
  oauthConnectUrl,
  spotifySearch,
  type SpotifySearchHit,
  bandsintownLookup,
  type BitDashStatus,
  bitDashStatus,
  bitDashLogin,
  bitDashDisconnect,
  bitFacebookUrl,
  gcalStatus,
  gcalAuthUrl,
  gcalSync,
  gcalDisconnect,
  openOAuthPopup,
  type GcalState,
} from '../../lib/settings';
import { fetchTopCities, clearDataCache, fetchShopifyBillingStatus, firstRunSync, fetchProStatus, startProCheckout, cancelPro } from '../../lib';
import type { ShopifyBillingStatus, ProPlanStatus } from '../../lib';
import { apiPost, apiGet, AUTH_WORKER } from '../../lib/api';
/* acpro-billing branding 2026-07-28 — the Pro block wears the real lockup. */
import { BrandWordmark, BrandProBadge } from './glyphs';
import { clearToken } from '../../lib/auth';
import { RoleLabel } from '../contacts-sections/RoleLabel';
import './settings.css';

/* staging-toggle 2026-07-06: the ONLY accounts that see the Staging-mode
   toggle in Settings → Account. UX gate, actual access control is the
   staging worker's LOGIN_ALLOWLIST (keep the two lists in sync). */
const STAGING_OPERATORS = new Set(['whitsettband@gmail.com', 'cryanward@icloud.com']);
const STAGING_API_BASE = 'https://cntrl-auth-staging.whitsettband.workers.dev';

type TabKey = 'profile' | 'connections' | 'account' | 'features';

/** Normalize the seat/system role for the data-hide-for-* checks. */
function roleOf(user: User | null): string {
  return String(user?.membership_role || user?.role || '').toLowerCase();
}
/* band-genres 2026-07-17 (Ryan: Whitsett's legacy slash-genre string differs
   from every application-era band; "add the 3 genre selector to artist profile
   section so they can change things if they ever want too"), the same 22
   chips the application form offers, saving straight to users.genre on the
   band root via /api/band/meta. That column is EXACTLY what Antenna's fan
   cards render, so a re-pick here normalizes legacy strings on the spot. */
const GENRE_CHOICES: readonly string[] = [
  'Rock', 'Pop', 'Country', 'Hip Hop', 'R&B', 'Indie', 'Alternative', 'Metal',
  'Punk', 'Folk', 'Electronic', 'Worship', 'Christian', 'Emo', 'Pop Punk',
  'Hardcore', 'Blues', 'Jazz', 'Soul', 'Reggae', 'Latin', 'Singer-Songwriter',
];

function GenrePicker(): JSX.Element {
  const [picked, setPicked] = useState<string[]>([]);
  const [raw, setRaw] = useState('');       // legacy string when it doesn't map to chips
  const [status, setStatus] = useState('');

  useEffect(() => {
    let alive = true;
    void apiGet<{ genre?: string }>('auth', '/api/band/meta')
      .then((r) => {
        if (!alive) return;
        const g = String(r?.genre || '');
        const parts = g.split(',').map((x) => x.trim()).filter(Boolean);
        const known = parts.filter((x) => GENRE_CHOICES.includes(x));
        setPicked(known.slice(0, 3));
        setRaw(known.length === parts.length ? '' : g);
      })
      .catch(() => { /* leave empty, picker still works */ });
    return () => { alive = false; };
  }, []);

  const toggle = (g: string): void => {
    const has = picked.includes(g);
    if (!has && picked.length >= 3) return;
    const next = has ? picked.filter((x) => x !== g) : [...picked, g];
    setPicked(next);
    if (next.length === 0) { setStatus('Pick at least one'); return; }
    setStatus('Saving…');
    void apiPost('auth', '/api/band/meta', { genres: next })
      .then(() => { setRaw(''); setStatus('Saved ✓'); window.setTimeout(() => setStatus(''), 1600); })
      .catch((e: unknown) => { setStatus(e instanceof Error ? e.message : 'Couldn’t save, retry'); });
  };

  return (
    <div className="settings-field">
      <label className="settings-field__label">
        Genres · max 3
        {status ? <span className="genre-pick__status">{status}</span> : null}
      </label>
      <p className="settings-field__hint">
        These show on your Antenna card and press kit.
        {raw ? ' Currently saved as “' + raw + '”, pick chips to update it.' : ''}
      </p>
      <ul className="chip-list">
        {GENRE_CHOICES.map((g) => {
          const on = picked.includes(g);
          const full = !on && picked.length >= 3;
          return (
            <li
              key={g}
              className={'chip chip--pick' + (on ? ' chip--picked' : '')}
              aria-disabled={full ? 'true' : undefined}
              onClick={() => { if (!full) toggle(g); }}
            >
              {g}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Artist Profile tab ───────────────────────────────────────────────────────

export function ProfileTab(): JSX.Element {
  const [profile, setProfile] = useState<BandProfile>(() => getProfileLocal());
  const [artistInput, setArtistInput] = useState('');
  const [saveLabel, setSaveLabel] = useState('Save Profile');

  // Hydrate from cloud on mount (cloud wins when it has content).
  useEffect(() => {
    let alive = true;
    void fetchCloudProfile().then((cloud) => {
      if (alive && profileHasContent(cloud)) setProfile(cloud as BandProfile);
    });
    return () => {
      alive = false;
    };
  }, []);

  const persist = useCallback((next: BandProfile) => {
    setProfile(next);
    void saveProfile(next);
  }, []);

  const addArtist = useCallback(
    (rawName: string) => {
      const name = rawName.trim();
      if (!name) return;
      const exists = profile.artists.some((a) => a.name.toLowerCase() === name.toLowerCase());
      if (exists) return;
      const pending: ComparableArtist = { name, listeners: null, status: 'validating' };
      const next = { ...profile, artists: [...profile.artists, pending] };
      persist(next);
      void validateArtist(name).then((res) => {
        setProfile((cur) => {
          const updated = {
            ...cur,
            artists: cur.artists.map((a) =>
              a.name.toLowerCase() === name.toLowerCase()
                ? { name: res?.name || a.name, listeners: res?.listeners ?? null, status: res?.status || 'red' }
                : a,
            ),
          };
          void saveProfile(updated);
          return updated;
        });
      });
    },
    [profile, persist],
  );

  const removeArtist = useCallback(
    (idx: number) => {
      persist({ ...profile, artists: profile.artists.filter((_, i) => i !== idx) });
    },
    [profile, persist],
  );

  const setField = useCallback(
    (key: string, value: string) => {
      persist({ ...profile, [key]: value });
    },
    [profile, persist],
  );

  const onSave = useCallback(async () => {
    setSaveLabel('Saving…');
    const ok = await saveProfile(profile);
    setSaveLabel(ok ? '✓ Saved' : 'Save failed, retry');
    window.setTimeout(() => setSaveLabel('Save Profile'), ok ? 1500 : 2400);
  }, [profile]);

  const onClear = useCallback(() => {
    if (!window.confirm('Clear the artist profile? This wipes comparable artists, sound, audience, goals, working, voice, and avoid fields.')) return;
    persist({ artists: [], sound: '', audience: '', goals: '', working: '', voice: '', avoid: '' });
  }, [persist]);

  return (
    <section className="settings-panel is-active" data-settings-panel="profile">
      <GenrePicker />
      <div className="settings-field">
        <label className="settings-field__label" htmlFor="profile-comparable">Comparable artists</label>
        <p className="settings-field__hint">Type an artist name and press Enter. Green = strong data, yellow = decent, red = limited.</p>
        <ul className="chip-list" id="profile-comparable-list">
          {profile.artists.length === 0 ? (
            <li style={{ opacity: 0.5, fontSize: '0.85rem', padding: '4px 0' }}>No artists yet, add a few to power AI features.</li>
          ) : (
            profile.artists.map((a, i) => {
              const tone =
                a.status === 'green' ? 'chip--success' : a.status === 'yellow' ? 'chip--warning' : a.status === 'red' ? 'chip--danger' : '';
              const sub = a.status === 'validating' ? 'checking…' : a.listeners != null ? fmtListeners(a.listeners) || 'no data' : 'unknown';
              return (
                <li className={'chip ' + tone} key={a.name + i}>
                  {a.name}
                  {sub ? <span className="chip__sub">{sub}</span> : null}
                  <button className="chip__remove" type="button" aria-label="Remove" onClick={() => removeArtist(i)}>×</button>
                </li>
              );
            })
          )}
        </ul>
        <input
          className="input"
          id="profile-comparable"
          placeholder="Type an artist name and press Enter…"
          value={artistInput}
          onChange={(e) => setArtistInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (!artistInput.trim()) return;
            addArtist(artistInput);
            setArtistInput('');
          }}
        />
      </div>

      {PROFILE_TEXT_FIELDS.map((f) => (
        <div className="settings-field" key={f.key}>
          <label className="settings-field__label" htmlFor={'profile-' + f.key}>{f.label}</label>
          <p className="settings-field__hint">{f.hint}</p>
          {f.multiline ? (
            <textarea
              className="input input--textarea"
              id={'profile-' + f.key}
              rows={f.key === 'working' ? 3 : 2}
              placeholder={f.placeholder}
              value={(profile[f.key as keyof BandProfile] as string) || ''}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          ) : (
            <input
              className="input"
              id={'profile-' + f.key}
              placeholder={f.placeholder}
              value={(profile[f.key as keyof BandProfile] as string) || ''}
              onChange={(e) => setField(f.key, e.target.value)}
            />
          )}
        </div>
      ))}

      <div className="settings-actions">
        <button className="btn-primary" type="button" onClick={() => void onSave()}>{saveLabel}</button>
        <button className="btn-link" type="button" onClick={onClear}>Clear</button>
      </div>
    </section>
  );
}

// ─── Connections tab ──────────────────────────────────────────────────────────

const ICONS: Record<string, string> = {
  spotify: '/icons/spotify-square-color-icon.svg',
  youtube: '/icons/youtube-color-icon.svg',
  tiktok: '/icons/tiktok-rounded-square-icon.svg',
  meta: '/icons/ig-instagram-icon.svg',
  bandsintown: '/icons/BIT_Avatar_RoundedSquare_Teal.svg',
};

function Pill({ v }: { readonly v: ReturnType<typeof connView> }): JSX.Element {
  return <span className={'live-pill' + (v.variant ? ' live-pill--' + v.variant : '')}>{v.label}</span>;
}

/* first-connect prime 2026-07-17 (Ryan: "on initial connection it needs to
   trigger a one time push so bands dont feel like 'somethings broken'"),
   the Songstats-backed panels (top songs, playlists, editorial, Apple) ride
   multi-hour caches that can hold a pre-connection EMPTY result, so a band's
   first day looks broken even though the pipe works. When a stats-driving
   connection id CHANGES, fire one cache-busting stats pull (body.nocache=1
   travels auth worker → stats worker and forces fresh Songstats/Spotify/Meta
   fetches, re-priming every cache with real data). fetchDashboardStats also
   fires the daily snapshot writer, seeding trends on day one. Throttled hard
  , nocache pulls are expensive against the Songstats quota. */
let _lastStatsPrime = 0;
function _statsIdsOf(c: ConnBlob): string {
  const g = (o: unknown, k: string): string => {
    const v2 = o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : '';
    return v2 == null ? '' : String(v2);
  };
  return [g(c.spotify, 'id'), g(c.youtube, 'id'), g(c.tiktok, 'id'), g(c.meta, 'pageId'), g(c.meta, 'igUserId')].join('|');
}
function primeStatsAfterConnect(prev: ConnBlob, next: ConnBlob): void {
  if (_statsIdsOf(prev) === _statsIdsOf(next)) return; // no stats-driving id changed
  const now = Date.now();
  if (now - _lastStatsPrime < 5 * 60_000) return;
  _lastStatsPrime = now;
  /* first-run-sync 2026-07-18 (Ryan: "make sure a new artist has a way to push
     ALL their data the first time"): the connect prime used to pull ONLY the
     social/audience stats. firstRunSync additionally warms radio/SXM (the one
     pipeline with no other client trigger) and the music compose, but only
     the FIRST time per account (the marker gates those expensive steps). On a
     later reconnect the marker is set, so this degrades to exactly the old
     stats-only pull. */
  void firstRunSync(false).catch(() => { /* best-effort */ });
}

/* merch-both 2026-07-18, the per-provider channel selector + interlock.
   merch-providers 2026-07-18 (Ryan: "there is a lot of merch platforms we
   will need to add over time"), generalized from the Shopify/Square pair to
   N providers. Each provider declares which channels it CAN serve (Fourthwall
   is online-only); a channel owned by any OTHER connected provider is
   disabled here. */
type MerchChan = 'both' | 'online' | 'pos';
type MerchProviderId = 'shopify' | 'square' | 'fourthwall';
const MERCH_PROVIDER_CAPS: Record<MerchProviderId, ReadonlyArray<MerchChan>> = {
  shopify: ['both', 'online', 'pos'],
  square: ['both', 'online', 'pos'],
  fourthwall: ['online'], // Fourthwall has no POS product, online only.
};
const MERCH_CHANNELS: ReadonlyArray<{ v: MerchChan; label: string }> = [
  { v: 'both', label: 'Both' },
  { v: 'online', label: 'Online' },
  { v: 'pos', label: 'POS' },
];
/** The concrete channel slots a value occupies ('both' → online+pos). */
function chanSlots(v: MerchChan | 'none'): ReadonlyArray<'online' | 'pos'> {
  if (v === 'both') return ['online', 'pos'];
  if (v === 'online' || v === 'pos') return [v];
  return [];
}
/** Every slot a provider is CAPABLE of serving. */
function capSlots(p: MerchProviderId): ReadonlyArray<'online' | 'pos'> {
  const out: Array<'online' | 'pos'> = [];
  MERCH_PROVIDER_CAPS[p].forEach((v) => chanSlots(v).forEach((x) => { if (!out.includes(x)) out.push(x); }));
  return out;
}
function channelAllowed(v: MerchChan, taken: ReadonlyArray<'online' | 'pos'>): boolean {
  return !chanSlots(v).some((x) => taken.includes(x));
}
function ChannelSelect({
  provider,
  value,
  taken,
  onPick,
}: {
  readonly provider: MerchProviderId;
  readonly value: MerchChan | 'none';
  /** Channel slots owned by OTHER connected providers. */
  readonly taken: ReadonlyArray<'online' | 'pos'>;
  readonly onPick: (p: MerchProviderId, v: MerchChan) => void;
}): JSX.Element {
  const caps = MERCH_PROVIDER_CAPS[provider];
  const choices = MERCH_CHANNELS.filter(({ v }) => caps.includes(v));
  const fullyOff = choices.every(({ v }) => !channelAllowed(v, taken));
  return (
    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {choices.map(({ v, label }) => {
        const disabled = !channelAllowed(v, taken);
        const active = value === v && !fullyOff;
        return (
          <button
            key={v}
            type="button"
            disabled={disabled}
            onClick={() => onPick(provider, v)}
            style={{
              padding: '5px 13px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              border: active ? '1px solid #111' : '1px solid var(--ac-line-strong, #ddd)',
              background: active ? '#111' : '#fff',
              color: active ? '#fff' : disabled ? 'var(--ac-t3, #aaa)' : '#111',
              opacity: disabled ? 0.45 : 1,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {label}
          </button>
        );
      })}
      {fullyOff && (
        <span style={{ fontSize: 11, color: 'var(--ac-t3)', marginLeft: 4 }}>Off · another store covers this channel</span>
      )}
    </div>
  );
}

export function ConnectionsTab({ user }: { readonly user: User | null }): JSX.Element {
  const [c, setC] = useState<ConnBlob>({});
  const [spotifyInput, setSpotifyInput] = useState('');
  const [youtubeInput, setYoutubeInput] = useState('');
  const [bitInput, setBitInput] = useState('');
  const [shopifyInput, setShopifyInput] = useState('');
  const [spotifyHits, setSpotifyHits] = useState<SpotifySearchHit[]>([]);
  const [helper, setHelper] = useState<Record<string, string>>({});
  const [ig, setIg] = useState('');
  const [tt, setTt] = useState('');
  const [th, setTh] = useState('');
  const [socialStatus, setSocialStatus] = useState('');
  const [gcal, setGcal] = useState<GcalState>('loading');
  const [gcalEmail, setGcalEmail] = useState('');
  const [gcalLast, setGcalLast] = useState('');
  /* first-run-sync 2026-07-18, the manual "Refresh all data" escape hatch. */
  const [syncState, setSyncState] = useState<'idle' | 'running' | 'done'>('idle');
  const [bitEmail, setBitEmail] = useState('');
  const [bitPass, setBitPass] = useState('');
  const [bitDash, setBitDash] = useState<BitDashStatus>({ connected: false });
  const [bitDashMsg, setBitDashMsg] = useState('');
  /* fourthwall-oauth 2026-07-18, the band's shop (slug or host) for the
     platform-app OAuth redirect. Credential paste stays as an API-level
     fallback but is no longer surfaced in the drawer. */
  const [fwShop, setFwShop] = useState('');

  // Load the connections blob + prefill public handles + GCal status.
  useEffect(() => {
    let alive = true;
    void fetchConnBlob().then((blob) => {
      if (!alive) return;
      setC(blob);
      const m = (blob.meta || {}) as Record<string, unknown>;
      const insta = (blob.instagram || {}) as Record<string, unknown>;
      const tik = (blob.tiktok || {}) as Record<string, unknown>;
      const thr = (blob.threads || {}) as Record<string, unknown>;
      const igv = (insta.handle || insta.username || m.igUsername || '') as string;
      setIg(igv ? '@' + String(igv).replace(/^@/, '') : '');
      const ttv = (tik.handle || tik.username || '') as string;
      setTt(ttv ? '@' + String(ttv).replace(/^@/, '') : '');
      const thv = (thr.handle || thr.username || '') as string;
      setTh(thv ? '@' + String(thv).replace(/^@/, '') : '');
    });
    void gcalStatus().then((s) => {
      if (!alive) return;
      setGcal(s.connected ? 'connected' : 'disconnected');
      setGcalEmail(s.email || '');
    });
    return () => {
      alive = false;
    };
  }, []);

  const setHelp = (k: string, msg: string) => setHelper((h) => ({ ...h, [k]: msg }));

  const cRef = useRef<ConnBlob>({});
  cRef.current = c;
  const persistConn = useCallback(async (next: ConnBlob) => {
    const prev = cRef.current;
    setC(next);
    const saved = await saveConnBlob(next);
    primeStatsAfterConnect(prev, next); // first-connect prime (fire-and-forget)
    return saved;
  }, []);

  // Spotify connect (URL/ID → save, else search).
  const spotifyConnect = useCallback(async () => {
    const raw = spotifyInput.trim();
    if (!raw) return setHelp('spotify', 'Paste an artist URL or ID first.');
    const id = parseSpotifyId(raw);
    if (id) {
      setHelp('spotify', 'Saving…');
      const ok = await persistConn({ ...c, spotify: { ...(c.spotify || {}), id } });
      setHelp('spotify', ok ? 'Saved.' : 'Save failed. Try again.');
      if (ok) setSpotifyInput('');
      return;
    }
    setHelp('spotify', 'Searching…');
    const hits = await spotifySearch(raw);
    if (!hits.length) return setHelp('spotify', 'No matches. Try the full Spotify URL.');
    setSpotifyHits(hits.slice(0, 5));
    setHelp('spotify', '');
  }, [spotifyInput, c, persistConn]);

  const pickSpotify = useCallback(
    async (hit: SpotifySearchHit) => {
      const ok = await persistConn({ ...c, spotify: { ...(c.spotify || {}), id: hit.id } });
      if (ok) {
        setSpotifyHits([]);
        setSpotifyInput('');
        setHelp('spotify', 'Saved.');
      }
    },
    [c, persistConn],
  );

  const youtubeConnect = useCallback(async () => {
    const parsed = parseYouTube(youtubeInput);
    if (!parsed) return setHelp('youtube', 'Paste a YouTube URL or @handle.');
    setHelp('youtube', 'Saving…');
    const ok = await persistConn({ ...c, youtube: { ...(c.youtube || {}), id: parsed } });
    setHelp('youtube', ok ? 'Saved.' : 'Save failed.');
    if (ok) setYoutubeInput('');
  }, [youtubeInput, c, persistConn]);

  const bitConnect = useCallback(async () => {
    const slug = parseBandsintown(bitInput);
    if (!slug) return setHelp('bandsintown', 'Paste a Bandsintown URL or name.');
    setHelp('bandsintown', 'Looking up…');
    const found = await bandsintownLookup(slug);
    if (!found) return setHelp('bandsintown', 'Artist not found.');
    const ok = await persistConn({ ...c, bandsintown: { ...(c.bandsintown || {}), artist: found.name } });
    setHelp('bandsintown', ok ? 'Saved.' : 'Save failed.');
    if (ok) setBitInput('');
  }, [bitInput, c, persistConn]);

  // past-shows-import 2026-07-18 (Ryan), pull the band's PAST Bandsintown events
  // into the schedule as confirmed past shows. The worker dedupes, so this is safe
  // to run more than once. On success we drop the data cache so Schedule re-reads.
  const [bitSyncing, setBitSyncing] = useState(false);
  const syncPastShows = useCallback(async () => {
    if (bitSyncing) return;
    setBitSyncing(true);
    setHelp('bandsintown', 'Loading past shows from Bandsintown…');
    try {
      const res = await fetch('https://auth.cntrlglobal.com/api/bandsintown/sync-past', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + (localStorage.getItem('cntrl_auth_token') || '') },
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.success) {
        const n = Number(data.added || 0);
        const skip = Number(data.skipped || 0);
        if (n > 0) {
          setHelp('bandsintown', 'Added ' + n + ' past show' + (n === 1 ? '' : 's')
            + (skip ? ' · ' + skip + ' already on your schedule' : '') + '.');
          clearDataCache(); // so Schedule re-reads with the new past shows
        } else if (data.total_past) {
          setHelp('bandsintown', 'All past shows are already on your schedule.');
        } else {
          setHelp('bandsintown', 'No past shows found on Bandsintown for this artist.');
        }
      } else {
        setHelp('bandsintown', (data && data.error) || 'Could not sync past shows.');
      }
    } catch {
      setHelp('bandsintown', 'Could not sync past shows, check your connection.');
    } finally {
      setBitSyncing(false);
    }
  }, [bitSyncing]);

  const disconnect = useCallback(
    async (platform: string) => {
      const next = { ...c };
      delete next[platform];
      await persistConn(next);
      if (platform === 'shopify' || platform === 'square' || platform === 'fourthwall') {
        await fetch('https://auth.cntrlglobal.com/api/' + platform + '/disconnect', {
          method: 'POST',
          headers: { Authorization: 'Bearer ' + (localStorage.getItem('cntrl_auth_token') || '') },
        }).catch(() => {});
      }
      // Make the disconnect instant: force the worker to recompute Top Cities
      // (refresh=1 busts its 6h cache) WITHOUT the removed platform, then drop the
      // React SWR snapshots so the dashboard re-reads fresh, no manual refresh.
      void fetchTopCities(true).catch(() => {});
      clearDataCache();
      // The Merch card derives its connected/blank state from the Shopify/Square
      // summary proxy; dropping the SWR snapshot doesn't reliably re-blank it in
      // place. Force a hard reload on a merch disconnect so the card returns to
      // its empty "Not connected" state immediately. (merch-disconnect-reload
      // 2026-07-08, Ryan)
      if (platform === 'shopify' || platform === 'square' || platform === 'fourthwall') {
        window.location.reload();
      }
    },
    [c, persistConn],
  );

  const shopifyConnect = useCallback(() => {
    const domain = shopifyInput.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!/\.myshopify\.com$/.test(domain)) {
      window.alert('Domain should look like yourstore.myshopify.com');
      return;
    }
    window.location.href = oauthConnectUrl('shopify', domain);
  }, [shopifyInput]);

  /* fourthwall-oauth 2026-07-18, send the band to THEIR shop's approve
     screen (the authorize URL lives on the shop's subdomain, so we need the
     shop first, same UX as Shopify's domain input). The worker callback
     saves tokens server-side and lands back on /app?fourthwall_connected=1. */
  const fourthwallOAuth = useCallback(() => {
    const raw = fwShop.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!raw || !/^[a-z0-9][a-z0-9-]*(\.fourthwall\.com)?$/.test(raw)) {
      window.alert('Enter your Fourthwall shop \u2014 like yourshop.fourthwall.com (or just \u201cyourshop\u201d).');
      return;
    }
    window.location.href = oauthConnectUrl('fourthwall', raw);
  }, [fwShop]);

  const redirectConnect = useCallback((platform: string) => {
    const url = oauthConnectUrl(platform);
    if (url) window.location.href = url;
  }, []);

  const saveHandles = useCallback(async () => {
    const clean = (v: string) => v.trim().replace(/^@/, '');
    const next: ConnBlob = { ...c };
    const insta = { ...(next.instagram || {}) } as Record<string, unknown>;
    const tik = { ...(next.tiktok || {}) } as Record<string, unknown>;
    const thr = { ...(next.threads || {}) } as Record<string, unknown>;
    if (clean(ig)) insta.handle = clean(ig);
    else delete insta.handle;
    if (clean(tt)) tik.username = clean(tt);
    else delete tik.username;
    if (clean(th)) thr.handle = clean(th);
    else delete thr.handle;
    next.instagram = Object.keys(insta).length ? insta : undefined;
    next.tiktok = Object.keys(tik).length ? tik : undefined;
    next.threads = Object.keys(thr).length ? thr : undefined;
    setSocialStatus('Saving…');
    const ok = await persistConn(next);
    setSocialStatus(ok ? 'Saved ✓' : 'Save failed, try again');
    window.setTimeout(() => setSocialStatus(''), 3000);
  }, [ig, tt, th, c, persistConn]);

  // GCal connect via popup + postMessage listener.
  const gcalConnect = useCallback(async () => {
    const url = await gcalAuthUrl();
    if (!url) {
      window.alert('Could not start Google Calendar connect, try again.');
      return;
    }
    openOAuthPopup(url, 'gcal-oauth');
  }, []);

  useEffect(() => {
    const onMsg = (e: MessageEvent): void => {
      const data = e.data as { ac?: string; payload?: { success?: boolean; email?: string; error?: string } };
      if (!data || data.ac !== 'gcal-oauth') return;
      const payload = data.payload || {};
      if (payload.success) {
        setGcal('connected');
        setGcalEmail(payload.email || '');
        void gcalSync().then((r) => r.ok && setGcalLast('Synced just now'));
      } else {
        setGcal('disconnected');
        window.alert('Could not connect Google Calendar: ' + (payload.error || 'unknown'));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const runGcalSync = useCallback(async () => {
    setGcalLast('Syncing…');
    const r = await gcalSync();
    if (!r.ok) return setGcalLast('Sync failed, try again');
    const parts: string[] = [];
    if (r.created) parts.push(r.created + ' added');
    if (r.updated) parts.push(r.updated + ' updated');
    if (!parts.length) parts.push('Already up to date');
    setGcalLast(parts.join(' · ') + ' · just now');
  }, []);

  const doGcalDisconnect = useCallback(async () => {
    if (!window.confirm('Disconnect Google Calendar? Your future shows will stop syncing. Already-synced events stay on your calendar.')) return;
    await gcalDisconnect();
    setGcal('disconnected');
  }, []);

  // Load the BIT push-session status once an artist is linked.
  const bitArtistLinked = !!(c.bandsintown && (c.bandsintown.artist || c.bandsintown.id));
  useEffect(() => {
    if (!bitArtistLinked) return;
    let alive = true;
    void bitDashStatus().then((s) => {
      if (alive) setBitDash(s);
    });
    return () => {
      alive = false;
    };
  }, [bitArtistLinked]);

  const doBitLogin = useCallback(async () => {
    if (!bitEmail.trim() || !bitPass) {
      setBitDashMsg('Enter your Bandsintown email and password.');
      return;
    }
    setBitDashMsg('Verifying credentials…');
    const res = await bitDashLogin(bitEmail.trim(), bitPass);
    setBitDashMsg(res.message);
    if (res.ok) {
      setBitDash({ connected: true, who: bitEmail.trim() });
      setBitEmail('');
      setBitPass('');
    }
  }, [bitEmail, bitPass]);

  const doBitFacebook = useCallback(() => {
    setBitDashMsg('Opening Facebook…');
    const popup = openOAuthPopup(bitFacebookUrl(), 'fb_bit_connect', 600, 700);
    const poll = window.setInterval(() => {
      if (popup && !popup.closed) return;
      window.clearInterval(poll);
      void bitDashStatus().then((s) => {
        setBitDash(s);
        setBitDashMsg(s.connected ? 'Connected via Facebook.' : 'Connection not detected, try again.');
      });
    }, 800);
  }, []);

  const doBitLogout = useCallback(async () => {
    if (!window.confirm('Disconnect the Bandsintown push session?')) return;
    await bitDashDisconnect();
    setBitDash({ connected: false });
    setBitDashMsg('');
  }, []);

  const sp = connView(c, 'spotify');
  const yt = connView(c, 'youtube');
  const tk = connView(c, 'tiktok');
  const mt = connView(c, 'meta');
  const sh = connView(c, 'shopify');
  const sq = connView(c, 'square');
  const fw = connView(c, 'fourthwall');
  const bit = connView(c, 'bandsintown');
  const filled = [ig, tt, th].filter((x) => x.trim()).length;

  /* Manual full re-sync. force=true bypasses the once-only marker; server-side
     per-band throttles still guard Songstats quota. Marks itself done so the
     label reads "Refreshed ✓" until the drawer is reopened. */
  const runFullSync = useCallback(() => {
    if (syncState === 'running') return;
    setSyncState('running');
    void firstRunSync(true).finally(() => setSyncState('done'));
  }, [syncState]);

  /* merch-both 2026-07-18, read a provider's assigned channel from the blob. */
  const channelOf = useCallback((p: MerchProviderId): MerchChan | 'none' => {
    const raw = String(((c[p] as Record<string, unknown> | undefined)?.channels as string) || 'both').toLowerCase();
    return raw === 'online' || raw === 'pos' || raw === 'none' ? (raw as MerchChan | 'none') : 'both';
  }, [c]);

  /* Channel slots owned by other CONNECTED providers, feeds each row's
     disabled state. (merch-providers 2026-07-18) */
  const takenFor = useCallback((p: MerchProviderId): ReadonlyArray<'online' | 'pos'> => {
    const conn: ReadonlyArray<readonly [MerchProviderId, boolean]> = [
      ['shopify', sh.connected], ['square', sq.connected], ['fourthwall', fw.connected],
    ];
    const out: Array<'online' | 'pos'> = [];
    conn.forEach(([id, isConn]) => {
      if (id === p || !isConn) return;
      chanSlots(channelOf(id)).forEach((x) => { if (!out.includes(x)) out.push(x); });
    });
    return out;
  }, [sh.connected, sq.connected, fw.connected, channelOf]);

  /* Set a provider's channel + enforce the interlock across ALL connected
     providers (merch-providers 2026-07-18, generalized from the pairwise
     shopify↔square version): any other provider whose channels overlap what
     this one just took is squeezed into whatever capable slots remain,
     both→the leftover channel, exact-overlap→'none'. Displaced providers are
     re-homed one at a time so two never land on the same slot. */
  const setMerchChannel = useCallback((provider: MerchProviderId, value: MerchChan) => {
    const connOf: Record<MerchProviderId, boolean> = {
      shopify: sh.connected, square: sq.connected, fourthwall: fw.connected,
    };
    const next: ConnBlob = { ...c };
    next[provider] = { ...((c[provider] as Record<string, unknown>) || {}), channels: value };
    const others = (Object.keys(connOf) as MerchProviderId[]).filter((p) => p !== provider && connOf[p]);
    const owned = new Set<string>(chanSlots(value));
    const displaced = others.filter((p) => chanSlots(channelOf(p)).some((x) => owned.has(x)));
    others.filter((p) => !displaced.includes(p)).forEach((p) => chanSlots(channelOf(p)).forEach((x) => owned.add(x)));
    displaced.forEach((p) => {
      const room = capSlots(p).filter((x) => !owned.has(x));
      const newVal: MerchChan | 'none' = room.length === 2 ? 'both' : room.length === 1 ? room[0] : 'none';
      room.forEach((x) => owned.add(x));
      next[p] = { ...((c[p] as Record<string, unknown>) || {}), channels: newVal };
    });
    void persistConn(next);
  }, [c, sh.connected, sq.connected, fw.connected, channelOf, persistConn]);

  return (
    <section className="settings-panel is-active" data-settings-panel="connections">
      {/* first-run-sync 2026-07-18 (Ryan: a new artist shouldn't sit half-empty
          for a day), the manual "push all my data" control. The auto path
          fires on first connect + first dashboard load; this is the escape
          hatch + re-sync. */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 14, padding: '14px 16px', marginBottom: 18, borderRadius: 12,
          background: 'var(--ac-accent-wash, rgba(139,26,43,0.06))',
          border: '1px solid var(--ac-accent-chip, rgba(139,26,43,0.12))',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>Your data</p>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, opacity: 0.7, lineHeight: 1.4 }}>
            Pull your latest streaming, radio, playlist and release data from every connected source.
          </p>
        </div>
        <button
          className="btn-accent btn-sm"
          type="button"
          disabled={syncState === 'running'}
          onClick={runFullSync}
          style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}
        >
          {syncState === 'running' ? 'Refreshing…' : syncState === 'done' ? 'Refreshed ✓' : 'Refresh all data'}
        </button>
      </div>

      <h3 className="settings-group-label">Analytics platforms</h3>

      {/* Spotify */}
      <article className="conn-card">
        <div className="conn-card__icon" style={{ background: 'transparent', padding: 0, overflow: 'hidden' }}>
          <img src={ICONS.spotify} alt="" width={36} height={36} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
        </div>
        <div className="conn-card__body">
          <header className="conn-card__head"><h4 className="conn-card__name">Spotify</h4><Pill v={sp} /></header>
          <p className="conn-card__desc">Stream counts, monthly listeners, playlist placements</p>
          {sp.meta && <p className="conn-card__meta">{sp.meta}</p>}
          {sp.connected ? (
            <div className="conn-card__actions"><button className="btn-ghost btn-sm" type="button" onClick={() => void disconnect('spotify')}>Disconnect</button></div>
          ) : (
            <div className="conn-card__form">
              <div className="settings-input-row">
                <input className="input" value={spotifyInput} onChange={(e) => setSpotifyInput(e.target.value)} placeholder="Paste Spotify artist URL or search by name…" />
                <button className="btn-primary btn-sm" type="button" onClick={() => void spotifyConnect()}>Connect</button>
              </div>
              {spotifyHits.length > 0 && (
                <div className="settings-search-results">
                  {spotifyHits.map((a) => (
                    <button className="settings-search-result" type="button" key={a.id} onClick={() => void pickSpotify(a)}>
                      <span className="settings-search-result__name">{a.name}</span>
                      <span className="settings-search-result__meta">{a.followers ? Number(a.followers).toLocaleString() + ' followers' : ''}</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="settings-helper">{helper.spotify || ''}</p>
            </div>
          )}
        </div>
      </article>

      {/* YouTube */}
      <article className="conn-card">
        <div className="conn-card__icon" style={{ background: 'transparent', padding: 0, overflow: 'hidden' }}>
          <img src={ICONS.youtube} alt="" width={36} height={36} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
        </div>
        <div className="conn-card__body">
          <header className="conn-card__head"><h4 className="conn-card__name">YouTube</h4><Pill v={yt} /></header>
          <p className="conn-card__desc">Video views, subscribers, channel analytics</p>
          {yt.meta && <p className="conn-card__meta">{yt.meta}</p>}
          {yt.connected ? (
            <div className="conn-card__actions"><button className="btn-ghost btn-sm" type="button" onClick={() => void disconnect('youtube')}>Disconnect</button></div>
          ) : (
            <div className="conn-card__form">
              <div className="settings-input-row">
                <input className="input" value={youtubeInput} onChange={(e) => setYoutubeInput(e.target.value)} placeholder="Paste YouTube channel URL or @handle…" />
                <button className="btn-primary btn-sm" type="button" onClick={() => void youtubeConnect()}>Connect</button>
              </div>
              <p className="settings-helper">{helper.youtube || ''}</p>
            </div>
          )}
        </div>
      </article>

      {/* TikTok */}
      <article className="conn-card">
        <div className="conn-card__icon" style={{ background: 'transparent', padding: 0, overflow: 'hidden' }}>
          <img src={ICONS.tiktok} alt="" width={36} height={36} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
        </div>
        <div className="conn-card__body">
          <header className="conn-card__head"><h4 className="conn-card__name">TikTok</h4><Pill v={tk} /></header>
          <p className="conn-card__desc">Video views, follower growth, best posting times, top videos</p>
          {tk.meta && <p className="conn-card__meta">{tk.meta}</p>}
          <div className="conn-card__actions">
            {tk.connected ? (
              <button className="btn-ghost btn-sm" type="button" onClick={() => void disconnect('tiktok')}>Disconnect</button>
            ) : (
              <button className="btn-primary btn-sm" type="button" onClick={() => redirectConnect('tiktok')}>Connect with TikTok</button>
            )}
          </div>
        </div>
      </article>

      {/* Meta, sits beside TikTok in the 2×2 analytics grid */}
      <article className="conn-card">
        <div className="conn-card__icon" style={{ background: 'transparent', padding: 0, overflow: 'hidden' }}>
          <img src={ICONS.meta} alt="" width={36} height={36} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
        </div>
        <div className="conn-card__body">
          <header className="conn-card__head"><h4 className="conn-card__name">Instagram + Facebook</h4><Pill v={mt} /></header>
          <p className="conn-card__desc">Followers, engagement, reach, Reels, post performance</p>
          {mt.meta && <p className="conn-card__meta">{mt.meta}</p>}
          {mt.connected ? (
            <div className="conn-card__actions"><button className="btn-ghost btn-sm" type="button" onClick={() => void disconnect('meta')}>Disconnect</button></div>
          ) : (
            <div className="conn-card__form">
              <button className="btn-primary btn-sm" type="button" onClick={() => redirectConnect('meta')}>Connect with Meta</button>
              <p className="settings-helper">Sign in with Facebook · choose your Page · grants Instagram + Facebook analytics access</p>
            </div>
          )}
        </div>
      </article>

      {/* Public social handles */}
      <h3 className="settings-group-label">Public social handles</h3>
      <p className="settings-intro settings-intro--small">Public handles for your release pages, Link in Bio, and EPK. Type the @handle (no URL needed).</p>
      <article className="conn-card conn-card--full">
        <div className="conn-card__icon" style={{ background: '#0e0c0b', color: '#fff', fontSize: 18, display: 'grid', placeItems: 'center', borderRadius: 10 }}>@</div>
        <div className="conn-card__body">
          <header className="conn-card__head"><h4 className="conn-card__name">Public handles</h4>
            <span className={'live-pill' + (filled === 3 ? ' live-pill--success' : filled > 0 ? ' live-pill--warning' : ' live-pill--muted')}>{filled ? filled + ' / 3 set' : 'not set'}</span>
          </header>
          <p className="conn-card__desc">Used by the public release pages + EPK + Link in Bio</p>
          <div className="conn-card__form" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--ac-color-t2)' }}>Instagram</span>
              <input className="input" value={ig} onChange={(e) => setIg(e.target.value)} placeholder="@yourband" autoCapitalize="off" autoComplete="off" />
            </label>
            <label style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--ac-color-t2)' }}>TikTok</span>
              <input className="input" value={tt} onChange={(e) => setTt(e.target.value)} placeholder="@yourband" autoCapitalize="off" autoComplete="off" />
            </label>
            <label style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 12, color: 'var(--ac-color-t2)' }}>Threads</span>
              <input className="input" value={th} onChange={(e) => setTh(e.target.value)} placeholder="@yourband" autoCapitalize="off" autoComplete="off" />
            </label>
            <button className="btn-primary btn-sm" type="button" onClick={() => void saveHandles()}>Save handles</button>
          </div>
          <span className="settings-helper" style={{ margin: '8px 0 0' }}>{socialStatus}</span>
        </div>
      </article>

      {/* Merch store, merch-both 2026-07-18 (Ryan): Shopify + Square can BOTH
          be connected, each scoped to a sales channel so nothing double-counts.
          The channel selectors interlock: a channel owned by one provider is
          disabled on the other. */}
      <h3 className="settings-group-label">Merch store</h3>
      {sh.connected && sq.connected && (
        <p className="settings-helper" style={{ margin: '0 0 12px' }}>
          Assign each store a sales channel so your merch totals don&rsquo;t double-count. Picking a channel here disables it on the other store.
        </p>
      )}

      {/* Shopify */}
      <div className="merch-panel" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 13, minWidth: 68 }}>Shopify</span>
        <Pill v={sh} />
        {sh.connected ? (
          <>
            <ChannelSelect provider="shopify" value={channelOf('shopify')} taken={takenFor('shopify')} onPick={setMerchChannel} />
            <button className="btn-ghost btn-sm" type="button" style={{ marginLeft: 'auto' }} onClick={() => void disconnect('shopify')}>Disconnect</button>
          </>
        ) : (
          <>
            <input className="input" style={{ flex: 1, minWidth: 200 }} value={shopifyInput} onChange={(e) => setShopifyInput(e.target.value)} placeholder="yourstore.myshopify.com" />
            <button className="btn-primary btn-sm" type="button" onClick={shopifyConnect}>Connect</button>
            <span className="settings-helper" style={{ flexBasis: '100%', margin: 0 }}>Online store orders + 30-day sales. Find yours: Shopify Admin → Settings → Domains.</span>
          </>
        )}
      </div>

      {/* Square */}
      <div className="merch-panel" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 13, minWidth: 68 }}>Square</span>
        <Pill v={sq} />
        {sq.connected ? (
          <>
            <ChannelSelect provider="square" value={channelOf('square')} taken={takenFor('square')} onPick={setMerchChannel} />
            <button className="btn-ghost btn-sm" type="button" style={{ marginLeft: 'auto' }} onClick={() => void disconnect('square')}>Disconnect</button>
          </>
        ) : (
          <>
            <span className="settings-intro settings-intro--small" style={{ flex: 1, minWidth: 160, margin: 0 }}>In-person POS + online sales via Square. 30-day totals.</span>
            <button className="btn-primary btn-sm" type="button" onClick={() => redirectConnect('square')}>Connect with Square</button>
          </>
        )}
      </div>

      {/* Fourthwall, fourthwall 2026-07-18 (Ryan: Mae sells online through
          Fourthwall). fourthwall-oauth: platform-app OAuth, band enters
          their shop, approves on Fourthwall, callback saves server-side.
          Online-only: the worker interlock moves Shopify/Square to POS. */}
      <div className="merch-panel" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, fontSize: 13, minWidth: 68 }}>Fourthwall</span>
        <Pill v={fw} />
        {fw.connected ? (
          <>
            <ChannelSelect provider="fourthwall" value={channelOf('fourthwall')} taken={takenFor('fourthwall')} onPick={setMerchChannel} />
            <button className="btn-ghost btn-sm" type="button" style={{ marginLeft: 'auto' }} onClick={() => void disconnect('fourthwall')}>Disconnect</button>
          </>
        ) : (
          <>
            <input className="input" style={{ flex: 1, minWidth: 200 }} value={fwShop} onChange={(e) => setFwShop(e.target.value)} placeholder="yourshop.fourthwall.com" />
            <button className="btn-primary btn-sm" type="button" onClick={fourthwallOAuth}>Connect with Fourthwall</button>
            <span className="settings-helper" style={{ flexBasis: '100%', margin: 0 }}>Online store sales via Fourthwall. You&rsquo;ll approve access in your Fourthwall dashboard &mdash; no keys to copy.</span>
          </>
        )}
      </div>

      {/* Live shows, Bandsintown */}
      <h3 className="settings-group-label">Live shows</h3>
      <article className="conn-card">
        <div className="conn-card__icon" style={{ background: 'transparent', padding: 0, overflow: 'hidden' }}>
          <img src={ICONS.bandsintown} alt="" width={36} height={36} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
        </div>
        <div className="conn-card__body">
          <header className="conn-card__head"><h4 className="conn-card__name">Bandsintown</h4><Pill v={bit} /></header>
          <p className="conn-card__desc">Sync upcoming shows, RSVPs, and tracked fan count</p>
          {bit.meta && <p className="conn-card__meta">{bit.meta}</p>}
          {bit.connected ? (
            <>
              <div className="conn-card__actions">
                <button className="btn-primary btn-sm" type="button" disabled={bitSyncing} onClick={() => void syncPastShows()}>
                  {bitSyncing ? 'Loading…' : 'Sync past shows'}
                </button>
                <button className="btn-ghost btn-sm" type="button" onClick={() => void disconnect('bandsintown')}>Disconnect</button>
              </div>
              {helper.bandsintown ? <p className="settings-helper">{helper.bandsintown}</p> : null}
            </>
          ) : (
            <div className="conn-card__form">
              <div className="settings-input-row">
                <input className="input" value={bitInput} onChange={(e) => setBitInput(e.target.value)} placeholder="Paste Bandsintown artist URL or band name…" />
                <button className="btn-primary btn-sm" type="button" onClick={() => void bitConnect()}>Connect</button>
              </div>
              <p className="settings-helper">{helper.bandsintown || ''}</p>
            </div>
          )}
        </div>
      </article>

      {/* BIT dashboard login, sits beside the Bandsintown artist card once
          an artist is linked (required to PUSH shows/festivals to BIT). */}
      {bitArtistLinked && (
        <article className="conn-card">
          <div className="conn-card__icon" style={{ background: 'transparent', padding: 0, overflow: 'hidden' }}>
            <img src={ICONS.bandsintown} alt="" width={36} height={36} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
          </div>
          <div className="conn-card__body">
            <header className="conn-card__head">
              <h4 className="conn-card__name">Bandsintown login</h4>
              {bitDash.connected && <span className="live-pill live-pill--success">Connected</span>}
            </header>
            <p className="conn-card__desc">Required to push your shows + festivals to Bandsintown. We never store your password, we use it once to mint a session token.</p>
            {bitDash.connected ? (
              <div className="conn-card__actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="conn-card__meta" style={{ flex: 1, margin: 0 }}>
                  {bitDash.via === 'facebook' ? 'Connected via Facebook' : bitDash.who ? 'Connected as ' + bitDash.who : 'Connected'}
                </span>
                <button className="btn-ghost btn-sm" type="button" onClick={() => void doBitLogout()}>Disconnect</button>
              </div>
            ) : (
              <div className="conn-card__form">
                <input className="input" type="email" autoComplete="off" placeholder="you@band.com" value={bitEmail} onChange={(e) => setBitEmail(e.target.value)} />
                <div className="settings-input-row" style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input className="input" style={{ flex: 1 }} type="password" autoComplete="off" placeholder="Bandsintown password" value={bitPass} onChange={(e) => setBitPass(e.target.value)} />
                  <button className="btn-primary btn-sm" type="button" onClick={() => void doBitLogin()}>Connect</button>
                </div>
                <button className="btn-ghost btn-sm" type="button" style={{ width: '100%', marginTop: 8 }} onClick={doBitFacebook}>Connect via Facebook</button>
                {bitDashMsg && <p className="settings-helper">{bitDashMsg}</p>}
              </div>
            )}
          </div>
        </article>
      )}

      {/* Google Calendar sync */}
      <h3 className="settings-group-label">Calendar sync</h3>
      <p className="settings-intro settings-intro--small">Sync your shows to Google Calendar, or subscribe from Apple Calendar (or any calendar app) below.</p>
      <div className="gcal-sync" data-gcal-state={gcal}>
        {gcal === 'loading' && (
          <div className="gcal-sync__status"><span className="gcal-sync__status-text">Checking connection…</span></div>
        )}
        {gcal === 'disconnected' && (
          <div className="gcal-sync__status">
            <div className="gcal-sync__connect-row">
              <button type="button" className="btn-primary gcal-sync__btn" onClick={() => void gcalConnect()}>
                <span>Connect Google Calendar</span>
              </button>
            </div>
            <p className="gcal-sync__hint">Approve calendar.events access on the next screen. We can create events on your calendar, nothing else.</p>
          </div>
        )}
        {gcal === 'connected' && (
          <div className="gcal-sync__status">
            <div className="gcal-sync__connected-row">
              <span className="gcal-sync__dot"></span>
              <div className="gcal-sync__connected-text">
                <span className="gcal-sync__connected-label">Connected to Google Calendar</span>
                <span className="gcal-sync__connected-email">{gcalEmail || 'your account'}</span>
              </div>
              <button type="button" className="btn-ghost btn-sm gcal-sync__action" onClick={() => void runGcalSync()}>Sync now</button>
            </div>
            <div className="gcal-sync__meta">
              <span className="gcal-sync__last-sync">{gcalLast}</span>
              <button type="button" className="gcal-sync__link gcal-sync__link--muted" onClick={() => void doGcalDisconnect()}>Disconnect</button>
            </div>
          </div>
        )}
      </div>

      {/* Apple Calendar / any-app ICS subscription (ical-web 2026-07-12, Ryan:
          "can we do iCal as well?"). The worker feed already existed
          (/api/calendar/<token>.ics, token minted per user, HEAD-probe
          friendly); this exposes it on web. webcal:// opens Apple Calendar's
          subscribe dialog directly on macOS; the copyable https URL covers
          Outlook and everything else. One-way by design. */}
      <IcsSubscribeCard />
      {/* user param retained for future role-scoped connector visibility */}
      <span hidden>{roleOf(user)}</span>
    </section>
  );
}

// ─── Apple Calendar / ICS subscription card (ical-web 2026-07-12) ────────────
// Lazily mints/loads the user's personal feed token (GET /api/calendar/me/info
//, the worker creates one on first ask), then offers: Subscribe in Apple
// Calendar (webcal://), Copy URL (any app), and Reset link (rotates the token;
// the old URL dies, calendars must re-subscribe). The token is secret-URL
// auth, treat it like a password, hence the reset affordance.
function IcsSubscribeCard(): JSX.Element {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let alive = true;
    void apiGet<{ success: boolean; token: string }>('auth', '/api/calendar/me/info')
      .then((r) => { if (alive) { if (r && r.token) { setToken(r.token); setState('ready'); } else setState('error'); } })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, []);
  const httpsUrl = AUTH_WORKER + '/api/calendar/' + token + '.ics';
  const webcalUrl = httpsUrl.replace(/^https:/, 'webcal:');
  const copy = (): void => {
    void navigator.clipboard?.writeText(httpsUrl).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }).catch(() => window.prompt('Copy your calendar URL:', httpsUrl));
  };
  const reset = (): void => {
    if (!window.confirm('Reset your calendar link? Anyone using the old URL (including your own subscribed calendars) stops getting updates and must re-subscribe.')) return;
    setState('loading');
    void apiPost<{ success: boolean; token: string }>('auth', '/api/calendar/me/regenerate', {})
      .then((r) => { if (r && r.token) { setToken(r.token); setState('ready'); } else setState('error'); })
      .catch(() => setState('error'));
  };
  return (
    <div className="gcal-sync" style={{ marginTop: 12 }}>
      <div className="gcal-sync__status">
        {state === 'loading' && <span className="gcal-sync__status-text">Loading your calendar link…</span>}
        {state === 'error' && <span className="gcal-sync__status-text">Couldn’t load your calendar link, reopen Settings to retry.</span>}
        {state === 'ready' && (
          <>
            <div className="gcal-sync__connect-row" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a className="btn-primary gcal-sync__btn" href={webcalUrl} style={{ textDecoration: 'none' }}>
                <span>Subscribe in Apple Calendar</span>
              </a>
              <button type="button" className="btn-ghost btn-sm" onClick={copy}>{copied ? 'Copied ✓' : 'Copy URL for any app'}</button>
            </div>
            <p className="gcal-sync__hint">
              Your shows as a live subscribed calendar, works in Apple Calendar, Outlook, anywhere. Apple refreshes about hourly.
              This private link is like a password: anyone with it can see your schedule.{' '}
              <button type="button" className="gcal-sync__link gcal-sync__link--muted" onClick={reset} style={{ padding: 0 }}>Reset link</button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Account tab ──────────────────────────────────────────────────────────────

export function AccountTab({ user }: { readonly user: User | null }): JSX.Element {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [conf, setConf] = useState('');
  const [pwLabel, setPwLabel] = useState('Update Password');
  const [openSupport, setOpenSupport] = useState<boolean>(user?.open_to_support !== 0);
  const isRoot = !!user?.is_band_primary;
  /* shopify-billing 2026-07-09, Shopify-billed accounts get a Billing row:
     status + a link to Shopify's hosted plan page (select/switch/cancel all
     happen there). Stripe/organic accounts: endpoint says not required, row
     renders their plan exactly as before. */
  const [shopifyBilling, setShopifyBilling] = useState<ShopifyBillingStatus | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchShopifyBillingStatus().then((sb) => { if (alive) setShopifyBilling(sb); });
    return () => { alive = false; };
  }, []);

  /* acpro-billing 2026-07-28 (Ryan) — Pro upgrade/downgrade block state.
     Artist-side eligibility: primary + AV (roster seats get the same block
     in the roster SettingsModal). Follows the shopify-1.2.2 plan-UI rules:
     never for shopify-billed, never for lifetime/beta/comp accounts. */
  const acctRoleKey = String(user?.membership_role || user?.role || '').toLowerCase();
  const proEligible = (isRoot || acctRoleKey === 'band_member_av')
    && user?.billing_source !== 'shopify'
    && !['lifetime', 'beta', 'comp'].includes(String(user?.plan || '').toLowerCase());
  const [proStatus, setProStatus] = useState<ProPlanStatus | null>(null);
  const [proBusy, setProBusy] = useState(false);
  const [proErr, setProErr] = useState('');
  const justUpgraded = ((): boolean => {
    try { return new URLSearchParams(window.location.search).get('pro') === 'upgraded'; } catch { return false; }
  })();
  useEffect(() => {
    if (!proEligible) return;
    let alive = true;
    void fetchProStatus().then((s) => { if (alive) setProStatus(s); });
    return () => { alive = false; };
  }, [proEligible]);
  const onProUpgrade = useCallback(async (cadence: 'annual' | 'monthly') => {
    setProBusy(true); setProErr('');
    const res = await startProCheckout(cadence);
    if (res.url) { window.location.href = res.url; return; }
    /* acpro-price-swap 2026-07-28 — existing subscription swapped in place;
       Pro is live immediately, no redirect. */
    if (res.swapped) { setProStatus(await fetchProStatus()); setProBusy(false); return; }
    setProErr(res.error || 'Could not start checkout.');
    setProBusy(false);
  }, []);
  const onProCancel = useCallback(async (resume: boolean) => {
    if (!resume && !window.confirm('Downgrade to standard Artist CNTRL? Pro features (Teams, Buses, Advance Docs) stop rendering — the data is kept and comes back if you re-upgrade.')) return;
    setProBusy(true); setProErr('');
    const res = await cancelPro(resume);
    if (res.ok) setProStatus(await fetchProStatus());
    else setProErr(res.error || 'Request failed.');
    setProBusy(false);
  }, []);
  // staging-toggle 2026-07-06: current override state (badge + api.ts read the same key)
  const stagingOn = ((): boolean => {
    try { return !!localStorage.getItem('ac:api:base'); } catch { return false; }
  })();

  useEffect(() => {
    setOpenSupport(user?.open_to_support !== 0);
  }, [user?.open_to_support]);

  const onToggleSupport = useCallback(async (checked: boolean) => {
    setOpenSupport(checked);
    const ok = await saveOpenToSupport(checked);
    if (!ok) setOpenSupport(!checked);
  }, []);

  const onUpdate = useCallback(async () => {
    if (!cur.trim() || !nw.trim() || !conf.trim()) return window.alert('Fill in all three password fields.');
    if (nw.trim().length < 8) return window.alert('New password must be at least 8 characters.');
    if (nw.trim() !== conf.trim()) return window.alert("New passwords don't match.");
    setPwLabel('Updating…');
    const res = await changePassword(cur.trim(), nw.trim());
    setPwLabel('Update Password');
    window.alert(res.message);
    if (res.ok) {
      setCur('');
      setNw('');
      setConf('');
    }
  }, [cur, nw, conf]);

  /* delete-account-desktop 2026-07-23 (Ryan: "there is no where on desktop for
     a user to delete their account"): wires the SAME /api/delete-account the
     iOS app uses — full _deleteUserCascade on the worker (11 child tables +
     users row, anonymize fallback). Double confirm: dialog + typed email. */
  const [deleting, setDeleting] = useState(false);
  const [deleteArmed, setDeleteArmed] = useState(false);
  const onDeleteAccount = useCallback(async () => {
    const typed = window.prompt('Type your account email to confirm deletion:');
    if (!typed || typed.trim().toLowerCase() !== String(user?.email || '').toLowerCase()) {
      if (typed !== null) window.alert('Email did not match — account NOT deleted.');
      return;
    }
    setDeleting(true);
    try {
      await apiPost('auth', '/api/delete-account');
      window.alert('Your account has been deleted.');
      await clearToken();
      window.location.href = '/app';
    } catch {
      window.alert('Delete failed — contact support and we will remove it manually.');
      setDeleting(false);
    }
  }, [user]);

  return (
    <section className="settings-panel is-active" data-settings-panel="account">
      <h3 className="settings-group-label">Your account</h3>
      <dl className="account-list">
        <div className="account-list__row"><dt>Your artist</dt><dd>{user?.band_name || user?.display_name || '-'}</dd></div>
        {(user?.membership_role || user?.role) && (
          <div className="account-list__row"><dt>Your role</dt><dd><RoleLabel role={user?.membership_role || user?.role || ''} /></dd></div>
        )}
        <div className="account-list__row"><dt>Email</dt><dd>{user?.email || '-'}</dd></div>
        {/* shopify-1.2.2 2026-07-09: Plan row shows ONLY when a real Stripe-billed
            subscription exists (plan_tier). Comped/lifetime accounts showed a
            static "Lifetime" badge that Shopify's reviewer read as a purchasable
            plan with disabled billing, the app has NO in-app charges, so no
            plan-like UI may imply one. */}
        {/* shopify-1.2.2 round 2 (2026-07-10): the reviewer STILL saw a
            "Lifetime" badge, their reused review account carries plan_tier +
            plan='lifetime', which slipped this gate. HARD RULE now: plan UI
            renders ONLY for Stripe-billed accounts with a real tier; never
            for shopify-billed, never for lifetime/beta/comp. */}
        {isRoot && !!(user as { plan_tier?: string } | null)?.plan_tier
          && user?.billing_source !== 'shopify'
          && !['lifetime', 'beta', 'comp'].includes(String(user?.plan || '').toLowerCase())
          && !['lifetime', 'beta', 'comp'].includes(String((user as { plan_tier?: string } | null)?.plan_tier || '').toLowerCase()) && (
          <div className="account-list__row">
            <dt>Plan</dt>
            <dd>
              <span className="plan-badge">{planLabel(user)}</span>
              {/* shopify-billing 2026-07-09 (Ryan): monthly Stripe roots can ask
                  for the annual rate from here. TRUE self-serve cadence switch
                  needs the Stripe subscription wiring (still stubbed), until
                  then this is an upgrade REQUEST, never a second checkout link
                  (a raw annual payment link would double-subscribe them). */}
              {user?.billing_source !== 'shopify' && String(user?.billing_cadence || '').toLowerCase() === 'monthly' && (
                <>
                  {' '}
                  <a
                    className="linkbtn"
                    href={'mailto:whitsettband@gmail.com?subject=' + encodeURIComponent('Switch me to annual billing') + '&body=' + encodeURIComponent('Please switch my Artist CNTRL subscription to annual ($120/mo, billed yearly). Account: ' + (user?.email || ''))}
                  >
                    Upgrade to annual · $120/mo →
                  </a>
                </>
              )}
            </dd>
          </div>
        )}
        {shopifyBilling?.billing_source === 'shopify' && (
          <div className="account-list__row">
            <dt>Billing</dt>
            <dd>
              {shopifyBilling.subscription_active ? 'Active · billed through Shopify' : 'No active plan'}
              {shopifyBilling.pricing_url && (
                <>
                  {' · '}
                  <a href={shopifyBilling.pricing_url} target="_blank" rel="noreferrer">
                    {shopifyBilling.subscription_active ? 'Manage or switch plan on Shopify' : 'Choose plan on Shopify'}
                  </a>
                </>
              )}
            </dd>
          </div>
        )}
        <div className="account-list__row"><dt>Member since</dt><dd>{memberSince(user)}</dd></div>
      </dl>

      {/* acpro-billing 2026-07-28 — self-serve Pro. Upgrade → Stripe Checkout
          redirect; the webhook flips plan_tier, so post-checkout state can lag
          a minute. Downgrade = cancel at period end (comped Pro flips now). */}
      {proEligible && (
        <>
          <h3 className="settings-pro-lockup" aria-label="Artist CNTRL Pro">
            <span className="lockup-word"><BrandWordmark /></span>
            <span className="lockup-chip"><BrandProBadge /></span>
          </h3>
          <div className="settings-field">
            {justUpgraded && proStatus && !proStatus.is_pro ? (
              <p className="settings-field__hint" style={{ margin: '0 0 10px' }}>
                Payment received — Pro unlocks within a minute. Refresh if it hasn&rsquo;t flipped.
              </p>
            ) : null}
            {!proStatus ? (
              <p className="settings-field__hint" style={{ margin: 0 }}>Checking plan…</p>
            ) : proStatus.is_pro ? (
              <>
                <p className="settings-field__hint" style={{ margin: '0 0 10px' }}>
                  This band is on <b>Artist CNTRL Pro</b>
                  {proStatus.cancel_at ? <> — downgrade scheduled; Pro runs through {new Date(proStatus.cancel_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}.</> : '.'}
                </p>
                {proStatus.cancel_at && proStatus.has_subscription ? (
                  <button className="btn-ghost" type="button" disabled={proBusy} onClick={() => void onProCancel(true)}>{proBusy ? 'Working…' : 'Keep Pro'}</button>
                ) : (
                  <button className="btn-ghost" type="button" disabled={proBusy} onClick={() => void onProCancel(false)}>{proBusy ? 'Working…' : 'Downgrade to standard'}</button>
                )}
              </>
            ) : (
              <>
                <p className="settings-field__hint" style={{ margin: '0 0 10px' }}>
                  Teams &amp; crew roles, Buses, department-tabbed Advance Docs with shareable venue links — built for artists playing 3K+ rooms.
                </p>
                {/* acpro-billing cadence 2026-07-28 — both live prices offered;
                    annual leads (mirrors standard AC's annual-first pricing). */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn-primary" type="button" disabled={proBusy} onClick={() => void onProUpgrade('annual')}>
                    {proBusy ? 'Opening checkout…' : 'Upgrade · $250/mo billed annually'}
                  </button>
                  <button className="btn-ghost" type="button" disabled={proBusy} onClick={() => void onProUpgrade('monthly')}>
                    $300 month-to-month
                  </button>
                </div>
              </>
            )}
            {proErr ? <p className="settings-field__hint" style={{ margin: '8px 0 0', color: '#8B1A2B' }}>{proErr}</p> : null}
          </div>
        </>
      )}

      <h3 className="settings-group-label">Change password</h3>
      <div className="pw-grid">
        <div className="settings-field">
          <label className="settings-field__label" htmlFor="account-current-password">Current password</label>
          <input className="input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="••••••••" />
        </div>
        <div className="settings-field">
          <label className="settings-field__label" htmlFor="account-new-password">New password</label>
          <input className="input" type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="••••••••" />
        </div>
        <div className="settings-field">
          <label className="settings-field__label" htmlFor="account-confirm-password">Confirm new password</label>
          <input className="input" type="password" value={conf} onChange={(e) => setConf(e.target.value)} placeholder="••••••••" />
        </div>
      </div>
      <button className="btn-ghost" type="button" onClick={() => void onUpdate()}>{pwLabel}</button>

      <h3 className="settings-group-label" style={{ color: '#8B1A2B' }}>Danger zone</h3>
      <div className="settings-field">
        <p className="settings-field__hint" style={{ margin: '0 0 10px' }}>
          Permanently delete your account and everything attached to it. This cannot be undone.
        </p>
        {!deleteArmed ? (
          <button className="btn-ghost" type="button" style={{ borderColor: '#8B1A2B', color: '#8B1A2B' }} onClick={() => setDeleteArmed(true)}>
            Delete account
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#8B1A2B' }}>Are you sure? This is permanent.</span>
            <button className="btn-ghost" type="button" disabled={deleting} style={{ background: '#8B1A2B', borderColor: '#8B1A2B', color: '#fff' }} onClick={() => void onDeleteAccount()}>
              {deleting ? 'Deleting…' : 'Yes, delete my account'}
            </button>
            <button className="btn-ghost" type="button" disabled={deleting} onClick={() => setDeleteArmed(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      <h3 className="settings-group-label">Booking discovery</h3>
      <div className="settings-field">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <label className="settings-field__label" htmlFor="account-open-to-support" style={{ marginBottom: 4 }}>Open to booking inquiries</label>
            <p className="settings-field__hint" style={{ margin: 0 }}>
              Allow booking agents on Artist CNTRL to discover your band and reach out. When off, you're hidden from agent search, only artists you already have a relationship with can contact you.
            </p>
          </div>
          <label className="ac-toggle" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              id="account-open-to-support"
              checked={openSupport}
              onChange={(e) => void onToggleSupport(e.target.checked)}
              style={{ appearance: 'none', width: 42, height: 24, background: openSupport ? 'var(--ac-color-accent,#8B1A2B)' : '#d1d5db', borderRadius: 999, position: 'relative', cursor: 'pointer', transition: 'background 0.15s', outline: 'none', border: 0, margin: 0 }}
            />
          </label>
        </div>
      </div>

      {/* staging-toggle 2026-07-06: visible ONLY to the two operator accounts.
          Flips the ac:api:base override (same mechanism as the console command
          + the amber STAGING badge). UX gate only, the server-side
          LOGIN_ALLOWLIST on the staging worker is the actual security. */}
      {STAGING_OPERATORS.has(String(user?.email || '').toLowerCase()) && (
        <>
          <h3 className="settings-group-label">Environment</h3>
          <div className="settings-field">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label className="settings-field__label" style={{ marginBottom: 4 }}>Staging mode</label>
                <p className="settings-field__hint" style={{ margin: 0 }}>
                  Point this browser at the staging environment (separate database, safe to test).
                  You&apos;ll be signed out and land on the staging login. The amber STAGING badge
                  shows whenever it&apos;s on. Only this browser is affected; users never see staging.
                </p>
              </div>
              <label className="ac-toggle" style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={stagingOn}
                  onChange={(e) => {
                    const on = e.target.checked;
                    const msg = on
                      ? 'Switch this browser to STAGING? You will need to sign in again.'
                      : 'Return this browser to PRODUCTION? You will need to sign in again.';
                    if (!window.confirm(msg)) return;
                    try {
                      if (on) localStorage.setItem('ac:api:base', STAGING_API_BASE);
                      else localStorage.removeItem('ac:api:base');
                    } catch { /* ignore */ }
                    window.location.reload();
                  }}
                  style={{ appearance: 'none', width: 42, height: 24, background: stagingOn ? '#b45309' : '#d1d5db', borderRadius: 999, position: 'relative', cursor: 'pointer', transition: 'background 0.15s', outline: 'none', border: 0, margin: 0 }}
                />
              </label>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// ─── Features tab (live roadmap + feature request) ──────────────────────────

/* live-roadmap 2026-07-27: the board now lives in ASSETS_KV features-list,
   managed from the CG admin Features screen (with per-platform tagging).
   GET stats /features is public and returns the raw array. We show items
   tagged 'Artist CNTRL' plus untagged legacy items; the static list below
   is ONLY the fallback while the KV board is empty or the fetch fails, so
   production never regresses to a blank roadmap. Once the KV board has AC
   items, it fully replaces this list — keep it frozen, edit in admin. */
interface LiveFeature { id?: string | number; name?: string; description?: string; d?: string; status?: string; category?: string; platform?: string; release?: string }
type RoadRow = { title: string; tag?: string; status: string; statusClass: string; desc?: string };
const FEAT_STATUS: Record<string, { label: string; cls: string }> = {
  considering: { label: 'Considering', cls: 'tag--soft-warning' },
  planned: { label: 'Planned', cls: '' },
  building: { label: 'In progress', cls: 'tag--soft-success' },
  completed: { label: 'Shipped', cls: 'tag--soft-info' },
};
const toRoadRow = (f: LiveFeature): RoadRow => {
  const st = FEAT_STATUS[f.status || 'considering'] || { label: f.status || 'Considering', cls: '' };
  return { title: f.name || '', tag: f.category || undefined, status: st.label, statusClass: st.cls, desc: f.description || f.d || undefined };
};
const ROADMAP: ReadonlyArray<RoadRow> = [
  { title: 'Guarantee Quote Builder', tag: 'Schedule', status: 'In progress', statusClass: 'tag--soft-success', desc: 'This will help you accurately price your self for a show based on previous data and expense' },
  { title: 'TikTok Integration', status: 'In progress', statusClass: 'tag--soft-success', desc: 'waiting on app approval' },
  { title: 'Meta Integration', status: 'In progress', statusClass: 'tag--soft-success', desc: 'waiting on app approval' },
  { title: 'Accounting Tab', status: 'Planned', statusClass: '', desc: 'A full system to help be fully aware of your finances' },
  { title: 'VIP scheduling features', status: 'Planned', statusClass: '' },
  { title: 'Merch Table Email List', tag: 'Connect', status: 'Planned', statusClass: '', desc: 'A QR Code fans can scan and add themselves to your email list' },
  { title: "Meta Ad's Integration", status: 'Considering', statusClass: 'tag--soft-warning', desc: 'will be able to push high performing content straight to a meta ad' },
  { title: 'Fix video viewing for AI in content', status: 'Shipped', statusClass: 'tag--soft-info' },
];
/* guides 2026-07-27 (Ryan: "these need to be changed to the tabs we actually
   have") — section keys are canonical and shared with the CG admin Guides
   screen (guides-list KV blob); the stale Connect/Schedule list predated the
   Marketing rename and missed Music + Antenna entirely. */
interface GuideVideo { id?: string; title?: string; url?: string }
type GuidesBlob = Record<string, GuideVideo[]>;
const GUIDE_SECTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'music', label: 'Music' },
  { key: 'releases', label: 'Releases' },
  { key: 'content', label: 'Content' },
  { key: 'marketing', label: 'Marketing' },
  { key: 'booking', label: 'Booking / Calendar' },
  { key: 'assets', label: 'Assets' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'antenna', label: 'Antenna' },
];

export function FeaturesTab({ user }: { readonly user: User | null }): JSX.Element {
  const [req, setReq] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [live, setLive] = useState<ReadonlyArray<RoadRow> | null>(null);
  const [guides, setGuides] = useState<GuidesBlob>({});
  const [openGuide, setOpenGuide] = useState<string | null>(null);
  useEffect(() => {
    let dead = false;
    apiGet<{ guides?: GuidesBlob }>('auth', '/api/guides')
      .then((j) => { if (!dead && j && j.guides) setGuides(j.guides); })
      .catch(() => { /* tiles just stay non-interactive */ });
    return () => { dead = true; };
  }, []);
  const guideTotal = GUIDE_SECTIONS.reduce((n, g) => n + (guides[g.key] || []).length, 0);
  useEffect(() => {
    let dead = false;
    apiGet<LiveFeature[]>('stats', '/features')
      .then((arr) => {
        if (dead || !Array.isArray(arr)) return;
        const mine = arr.filter((f) => f && f.name && (!f.platform || f.platform === 'Artist CNTRL'));
        if (mine.length > 0) setLive(mine.map(toRoadRow));
      })
      .catch(() => { /* keep static fallback */ });
    return () => { dead = true; };
  }, []);
  const roadmap = live || ROADMAP;
  // audit 2026-07-02: "Submit Request" only did setSent(true), nothing was
  // ever sent. POST the stats worker's live /feature-requests endpoint
  // ({ text, bandName, email }); on failure show an error, not fake success.
  const submit = (): void => {
    const text = req.trim();
    if (!text || busy || sent) return;
    setBusy(true);
    setErr(null);
    apiPost('stats', '/feature-requests', {
      text,
      bandName: user?.band_name || user?.display_name || 'Anonymous',
      email: user?.email || '',
    })
      .then(() => setSent(true))
      .catch(() => setErr('Could not submit your request, please try again.'))
      .finally(() => setBusy(false));
  };
  return (
    <section className="settings-panel is-active" data-settings-panel="features">
      <p className="settings-intro">See what we're building and request new features or integrations.</p>
      <h3 className="settings-group-label">Guides</h3>
      <p className="settings-intro settings-intro--small">Learn how to use each section of Artist CNTRL to its fullest.</p>
      <ul className="guide-grid">
        {/* audit 2026-07-02: the guide tiles were <button>s with no onClick,
            the guides don't exist yet (helper line below says "coming soon").
            Render as non-interactive divs (same classes/styling) so they don't
            look tappable; restore buttons when the video guides ship. */}
        {GUIDE_SECTIONS.map((g) => {
          const vids = guides[g.key] || [];
          if (vids.length === 0) {
            return <li key={g.key}><div className="guide-tile" style={{ cursor: 'default', opacity: guideTotal > 0 ? 0.55 : 1 }}>▸ {g.label}</div></li>;
          }
          const open = openGuide === g.key;
          return (
            <li key={g.key}>
              <button type="button" className="guide-tile" style={{ width: '100%', textAlign: 'left' }} aria-expanded={open}
                onClick={() => setOpenGuide(open ? null : g.key)}>
                {open ? '▾' : '▸'} {g.label} <span style={{ opacity: 0.55, fontWeight: 400 }}>({vids.length})</span>
              </button>
              {open && (
                <ul style={{ listStyle: 'none', margin: '6px 0 2px', padding: '0 0 0 18px', display: 'grid', gap: 6 }}>
                  {vids.map((v, i) => (
                    <li key={String(v.id ?? i)}>
                      <a href={v.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem', textDecoration: 'underline' }}>▶ {v.title || 'Watch guide'}</a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      {guideTotal === 0 && <p className="settings-helper settings-helper--center">Video guides coming soon</p>}

      <h3 className="settings-group-label">Roadmap</h3>
      <ul className="roadmap">
        {roadmap.map((r) => (
          <li className="roadmap-item" key={r.title}>
            <p className="roadmap-item__title">{r.title}{r.tag ? <span className="tag"> {r.tag}</span> : null}</p>
            <p className="roadmap-item__row"><span className={'tag ' + r.statusClass}>{r.status}</span>{r.desc ? <span className="roadmap-item__desc"> {r.desc}</span> : null}</p>
          </li>
        ))}
      </ul>

      <h3 className="settings-group-label">Request a feature</h3>
      <p className="settings-intro settings-intro--small">Have an idea for a feature or integration? Let us know.</p>
      <textarea className="input input--textarea" rows={4} placeholder="Describe the feature or integration you'd like to see…" value={req} onChange={(e) => setReq(e.target.value)} />
      <button className="btn-primary" type="button" disabled={busy} onClick={submit}>{sent ? '✓ Submitted' : busy ? 'Submitting…' : 'Submit Request'}</button>
      {err && <p className="settings-helper" role="alert" style={{ color: 'var(--ac-color-accent, #8B1A2B)' }}>{err}</p>}
    </section>
  );
}

// ─── Drawer shell ─────────────────────────────────────────────────────────────

export interface SettingsDrawerProps {
  readonly user: User | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

export function SettingsDrawer({ user, open, onClose }: SettingsDrawerProps): JSX.Element {
  const [tab, setTab] = useState<TabKey>('profile');
  const role = roleOf(user);
  /* member-view-cleanup 2026-07-19 (Ryan: member seats "should not have the
     connections tab at all. or the artist profile. So just account &
     features"), a regular band-member seat manages nothing band-wide, so
     Connections + Artist Profile are hidden for them. AV seats keep
     everything (AV = full primary authority, 2026-07-18 ruling); org/label
     seats are untouched. */
  const isLimitedMember = role === 'band_member' || role === 'content';
  const hideProfile = isLimitedMember;
  const hideConnections = isLimitedMember;
  const hideFeatures = role === 'content';
  /* A hidden tab can still be the *selected* one (default is 'profile'),
     resolve to Account so a member never renders a tab they can't see. */
  const activeTab: TabKey =
    (tab === 'profile' && hideProfile) || (tab === 'connections' && hideConnections)
      ? 'account'
      : tab;

  // Esc closes the drawer.
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc, true);
    return () => document.removeEventListener('keydown', onEsc, true);
  }, [open, onClose]);

  // Render nothing when closed, no inline panel, no invisible overlay that
  // could swallow clicks on the gear icon.
  if (!open) return <></>;

  const tabs: ReadonlyArray<{ key: TabKey; label: string; hidden: boolean }> = [
    { key: 'profile', label: 'Artist Profile', hidden: hideProfile },
    { key: 'connections', label: 'Connections', hidden: hideConnections },
    { key: 'account', label: 'Account', hidden: false },
    { key: 'features', label: 'Features', hidden: hideFeatures },
  ];

  // Portal to <body> so the fixed overlay escapes .topnav's containing block
  // (the nav uses backdrop-filter, which would otherwise size/position us
  // relative to the ~70px nav instead of the viewport).
  return createPortal(
    <div className="settings-drawer-overlay" onClick={onClose}>
      <aside className="settings-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="settings-drawer__head">
          <div className="settings-drawer__brand"><span className="settings-drawer__title">Settings</span></div>
          <button className="icon-btn settings-drawer__close" type="button" aria-label="Close" onClick={onClose}>×</button>
        </header>

        <nav className="settings-tabs" role="tablist">
          {tabs.filter((t) => !t.hidden).map((t) => (
            <button
              key={t.key}
              className={'settings-tabs__tab' + (activeTab === t.key ? ' is-active' : '')}
              role="tab"
              type="button"
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="settings-body">
          {activeTab === 'profile' && !hideProfile && <ProfileTab />}
          {activeTab === 'connections' && !hideConnections && <ConnectionsTab user={user} />}
          {activeTab === 'account' && <AccountTab user={user} />}
          {activeTab === 'features' && !hideFeatures && <FeaturesTab user={user} />}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
